/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Store } from '../client';
import type { QueryDict, TokenRefreshFunction } from '../http';
import type { RealtimeOptions } from '../models/realtime/interface';
import type { RoomNameState } from '../models/room/room';
import type { XHubChatScheduler } from '../scheduler/scheduler';
import type { SlidingSync } from '../sync';
import type { Logger } from '../utils/logger';

export type UserInfo = {
  displayName?: string;
  avatarUrl?: string;
  description?: string;
};

export type ICreateClientOpts = {
  baseUrl: string;

  /**
   * The data store used for sync data from the server. If not specified,
   * this client will not store any HTTP responses. The `createClient` helper
   * will create a default store if needed.
   */
  store?: Store;

  /**
   * The scheduler to use. If not
   * specified, this client will not retry requests on failure. This client
   * will supply its own processing function to
   * {@link XHubChatScheduler#setProcessFunction}.
   */
  scheduler?: XHubChatScheduler;

  /**
   * The function to invoke for HTTP requests.
   * Most supported environments have a global `fetch` registered to which this will fall back.
   */
  fetchFn?: typeof globalThis.fetch;

  userId?: string;

  /**
   * Information about the user for
   * identifying the client to the server.
   */
  userInfo?: UserInfo;

  /**
   * A unique identifier for this device; used for tracking things like crypto
   * keys and access tokens. If not specified, end-to-end encryption will be
   * disabled.
   */
  deviceId?: string;
  accessToken?: string;

  /**
   * The default maximum amount of
   * time to wait before timing out HTTP requests. If not specified, there is no timeout.
   */
  localTimeoutMs?: number;

  /**
   * Set to false to send the access token to the server via a query parameter rather
   * than the Authorization HTTP header.
   *
   * Note that as of v1.11 of the XHubChat spec, sending the access token via a query
   * is deprecated.
   *
   * Default true.
   */
  useAuthorizationHeader?: boolean;

  /**
   * Function used to attempt refreshing access and refresh tokens
   * Called by http-api when a possibly expired token is encountered
   * and a refreshToken is found
   */
  tokenRefreshFunction?: TokenRefreshFunction;

  /**
   * Set to true to enable
   * improved timeline support, see {@link XHubChatClient#getEventTimeline}.
   * It is disabled by default for compatibility with older clients - in particular to
   * maintain support for back-paginating the live timeline after a '/sync'
   * result with a gap.
   */
  timelineSupport?: boolean;

  /**
   * Extra query parameters to append
   * to all requests with this client. Useful for application services which require
   * `?user_id=`.
   */
  queryParams?: QueryDict;

  /**
   * Method to generate room names for empty rooms and rooms names based on membership.
   * Defaults to a built-in English handler with basic pluralization.
   */
  roomNameGenerator?: (roomId: string, state: RoomNameState) => string | null;

  /**
   * A logger to associate with this XHubChatClient.
   * Defaults to the built-in global logger; see {@link DebugLogger} for an alternative.
   */
  logger?: Logger;

  /** Centrifuge options for realtime updates */
  realtime: Omit<RealtimeOptions, 'centrifugeFactory'>;

  /**
   * This requires param to server can identify the client application.
   * @example { api_key: $API_KEY }
   */
  clientParams?: Record<string, string>;
};

export type IStartClientOpts = {
  /**
   * The event `limit=` to apply to initial sync. Default: 8.
   */
  initialSyncLimit?: number;

  /**
   * Controls where pending messages appear in a room's timeline. If "<b>chronological</b>", messages will
   * appear in the timeline when the call to `sendEvent` was made. If "<b>detached</b>",
   * pending messages will appear in a separate list, accessible via {@link Room#getPendingEvents}.
   * Default: "chronological".
   */
  pendingEventOrdering?: PendingEventOrdering;

  /**
   * True to perform syncing without automatically updating presence.
   */
  disablePresence?: boolean;

  /**
   * True to not load all membership events during initial sync but fetch them when needed by calling
   * `loadOutOfBandMembers` This will override the filter option at this moment.
   */
  lazyLoadMembers?: boolean;

  /**
   * Will organize events in threaded conversations when
   * a thread relation is encountered
   */
  threadSupport?: boolean;

  /**
   * Sliding sync for sync data
   */
  slidingSync: SlidingSync;
};

/**
 * @field Chronological - Pending events are inserted into the timeline in chronological order.
 * @field Detached - Pending events are kept in a separate list and not inserted into the timeline.
 */
export enum PendingEventOrdering {
  Chronological = 'chronological',
  Detached = 'detached',
}

export const TempMessagePrefix = '~xh';

export type Contractor = {
  id: string;
  name: string;
  description: string;
  created_at: string;
  channel_slug: string;
  logo: null;
  is_contact: boolean;
  updated_at: string;
  active: string;
};

export type IUserDirectoryResponse = {
  results: {
    user_id: string;
    display_name?: string;
    avatar_url?: string;
  }[];
  limited: boolean;
};
